import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Clock, HelpCircle, RefreshCw, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { setMemoryToken } from '../api/api';

const API = '/api';

const DevicePending = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(false);
  const [checkMessage, setCheckMessage] = useState('');

  const checkAuthorization = useCallback(async (silent = false) => {
    if (!silent) {
      setChecking(true);
      setCheckMessage('');
    }
    
    try {
      const storedCredentials = sessionStorage.getItem('pendingLogin');
      if (!storedCredentials) {
        if (!silent) {
          setCheckMessage('No hay credenciales guardadas. Por favor vuelve al login.');
        }
        setChecking(false);
        return;
      }

      const credentials = JSON.parse(storedCredentials);
      const response = await axios.post(`${API}/auth/login`, credentials, { withCredentials: true });
      
      if (response.data.device_authorized) {
        if (response.data.access_token) {
          setMemoryToken(response.data.access_token);
        }
        sessionStorage.removeItem('pendingLogin');
        
        if (!silent) {
          setCheckMessage('Dispositivo autorizado! Redirigiendo...');
        }
        
        setTimeout(() => {
          navigate('/dashboard');
          window.location.reload();
        }, 1000);
      }
    } catch (err) {
      if (!silent) {
        if (err.response?.status === 403) {
          setCheckMessage('Tu dispositivo aun esta pendiente de autorizacion.');
        } else {
          setCheckMessage('Error al verificar. Intenta nuevamente.');
        }
      }
    } finally {
      if (!silent) setChecking(false);
    }
  }, [navigate]);

  useEffect(() => {
    const interval = setInterval(() => {
      checkAuthorization(true);
    }, 10000);
    return () => clearInterval(interval);
  }, [checkAuthorization]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-500 via-blue-500 to-blue-600 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <div className="text-center mb-6">
            <div className="bg-amber-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-10 h-10 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2" data-testid="device-pending-title">
              Dispositivo Pendiente de Autorizacion
            </h2>
            <p className="text-gray-600">
              Tu dispositivo ha sido registrado y esta pendiente de autorizacion por un administrador.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Clock className="w-5 h-5 text-blue-500 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-800">Que sucede ahora?</h3>
                <ul className="mt-2 space-y-1 text-sm text-blue-700">
                  <li>1. Un administrador revisara tu solicitud</li>
                  <li>2. Una vez autorizado, podras acceder al sistema</li>
                  <li>3. Esta pagina se actualizara automaticamente</li>
                </ul>
              </div>
            </div>
          </div>

          {checkMessage && (
            <div className={`p-3 rounded-lg mb-4 text-center ${
              checkMessage.includes('autorizado') ? 'bg-green-100 text-green-700' : 
              checkMessage.includes('pendiente') ? 'bg-yellow-100 text-yellow-700' : 
              'bg-red-100 text-red-700'
            }`} data-testid="device-check-message">
              {checkMessage}
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => checkAuthorization(false)}
              disabled={checking}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              data-testid="check-authorization-btn"
            >
              {checking ? (
                <RefreshCw className="w-5 h-5 animate-spin" />
              ) : (
                <CheckCircle className="w-5 h-5" />
              )}
              <span>{checking ? 'Verificando...' : 'Verificar Autorizacion'}</span>
            </button>

            <button
              onClick={() => {
                sessionStorage.removeItem('pendingLogin');
                navigate('/login');
              }}
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              data-testid="back-to-login-btn"
            >
              <HelpCircle className="w-5 h-5" />
              <span>Volver al Login</span>
            </button>
          </div>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Esta pagina verifica automaticamente cada 10 segundos</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DevicePending;
